import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SaleItemService } from '../sale-item.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';

@Component({
  selector: 'app-group-item-edit-popup',
  templateUrl: './group-item-edit-popup.component.html',
  styleUrls: ['./group-item-edit-popup.component.scss'],
  standalone: false
})
export class GroupItemEditPopupComponent implements OnInit {
  editForm: FormGroup;
  isLoading = false;
  itemClasses: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<GroupItemEditPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { groupItemId: number, groupName: string },
    private fb: FormBuilder,
    private saleItemService: SaleItemService,
    private snackBarService: SnackBarService
  ) {
    this.editForm = this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(10)]],
      name: ['', [Validators.required, Validators.maxLength(50)]],
      itemClass: ['', Validators.required],
      displayOrder: ['', [Validators.required, Validators.pattern(/^[0-9]*$/)]],
      useMultiColor: [false],
      backgroundColor: ['#A9A9A9'],
      foregroundColor: ['#FFFFFF'],
      backgroundColor2: ['#A9A9A9'],

      // Hot Items
      hotItem1Enabled: [false],
      hotItem1DisplayOrder: [{ value: '', disabled: true }],
      hotItem1MenuNumber: [{ value: '', disabled: true }],

      hotItem2Enabled: [false],
      hotItem2DisplayOrder: [{ value: '', disabled: true }],
      hotItem2MenuNumber: [{ value: '', disabled: true }],

      hotItem3Enabled: [false],
      hotItem3DisplayOrder: [{ value: '', disabled: true }],
      hotItem3MenuNumber: [{ value: '', disabled: true }],

      driveThoughEnabled: [false],
      driveThoughDisplayOrder: [{ value: '', disabled: true }],
      driveThoughMenuNumber: [{ value: '', disabled: true }]
    });
  }

  ngOnInit(): void {
    this.loadItemClasses();
    
    // Only load group item data if we're editing an existing item
    if (this.isEditMode) {
      this.loadGroupItemData();
    }

    // 🔹 Handle enabling/disabling fields when checkboxes change
    this.setupHotItemCheckboxHandlers();
  }

  /** Automatically handle enable/disable for all hot item inputs */
  private setupHotItemCheckboxHandlers(): void {
    const pairs = [
      { checkbox: 'hotItem1Enabled', fields: ['hotItem1DisplayOrder', 'hotItem1MenuNumber'] },
      { checkbox: 'hotItem2Enabled', fields: ['hotItem2DisplayOrder', 'hotItem2MenuNumber'] },
      { checkbox: 'hotItem3Enabled', fields: ['hotItem3DisplayOrder', 'hotItem3MenuNumber'] },
      { checkbox: 'driveThoughEnabled', fields: ['driveThoughDisplayOrder', 'driveThoughMenuNumber'] }
    ];

    pairs.forEach(pair => {
      this.editForm.get(pair.checkbox)?.valueChanges.subscribe(enabled => {
        pair.fields.forEach(field => {
          const control = this.editForm.get(field);
          if (enabled) {
            control?.enable();
          } else {
            control?.disable();
            control?.reset();
          }
        });
      });
    });
  }

  loadItemClasses(): void {
    this.saleItemService.getAllItemClasses().subscribe({
      next: (response: any) => {
        if (response.status === 'SUCCESS' && response.data) {
          this.itemClasses = response.data.item_classes;
        }
      },
      error: () => {
        const snackBarData: SnackBarData = {
          message: 'Failed to load item classes',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  loadGroupItemData(): void {
    this.isLoading = true;

    this.saleItemService.getSaleItemById(this.data.groupItemId).subscribe({
      next: (response: any) => {
        if (response.status === 'SUCCESS' && response.data) {
          const item = response.data.item;
          this.editForm.patchValue({
            code: item.code || '',
            name: item.name || '',
            itemClass: item.sub_class_id || '',
            displayOrder: item.display_order || 0,
            useMultiColor: item.use_multi_color === 1,
            backgroundColor: item.bg_color || '#A9A9A9',
            foregroundColor: item.fg_color || '#FFFFFF',
            backgroundColor2: item.bg_color2 || '#A9A9A9',
            hotItem1Enabled: item.is_hot_item_1 === 1,
            hotItem1DisplayOrder: item.hot_item_1_display_order || '',
            hotItem1MenuNumber: item.hot_item_1_menu_number || '',
            hotItem2Enabled: item.is_hot_item_2 === 1,
            hotItem2DisplayOrder: item.hot_item_2_display_order || '',
            hotItem2MenuNumber: item.hot_item_2_menu_number || '',
            hotItem3Enabled: item.is_hot_item_3 === 1,
            hotItem3DisplayOrder: item.hot_item_3_display_order || '',
            hotItem3MenuNumber: item.hot_item_3_menu_number || '',
            driveThoughEnabled: item.is_dt_item === 1,
            driveThoughDisplayOrder: item.dt_display_order || '',
            driveThoughMenuNumber: item.dt_menu_number || ''
          });

          // Enable/disable after patchValue
          this.setupInitialHotItemControlStates();
        }
        this.isLoading = false;
      },
      error: () => {
        const snackBarData: SnackBarData = {
          message: 'Failed to load group item data',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
        this.isLoading = false;
      }
    });
  }

  /** Ensure correct enable/disable states after loading existing data */
  private setupInitialHotItemControlStates(): void {
    const pairs = [
      { checkbox: 'hotItem1Enabled', fields: ['hotItem1DisplayOrder', 'hotItem1MenuNumber'] },
      { checkbox: 'hotItem2Enabled', fields: ['hotItem2DisplayOrder', 'hotItem2MenuNumber'] },
      { checkbox: 'hotItem3Enabled', fields: ['hotItem3DisplayOrder', 'hotItem3MenuNumber'] },
      { checkbox: 'driveThoughEnabled', fields: ['driveThoughDisplayOrder', 'driveThoughMenuNumber'] }
    ];

    pairs.forEach(pair => {
      const enabled = this.editForm.get(pair.checkbox)?.value;
      pair.fields.forEach(field => {
        const control = this.editForm.get(field);
        if (enabled) {
          control?.enable();
        } else {
          control?.disable();
        }
      });
    });
  }

  onSubmit(): void {
    if (this.editForm.valid) {
      const formData = this.editForm.getRawValue(); // ✅ getRawValue to include disabled controls

      const itemData = {
        id: this.data.groupItemId,
        code: formData.code,
        name: formData.name,
        sub_class_id: formData.itemClass,
        display_order: formData.displayOrder,
        item_type: 1,
        is_group_item:1,
        use_multi_color: formData.useMultiColor ? 1 : 0,
        bg_color: formData.backgroundColor,
        fg_color: formData.foregroundColor,
        bg_color2: formData.backgroundColor2,
        is_hot_item_1: formData.hotItem1Enabled ? 1 : 0,
        hot_item_1_display_order: formData.hotItem1DisplayOrder || null,
        hot_item_1_menu_number: formData.hotItem1MenuNumber || null,
        is_hot_item_2: formData.hotItem2Enabled ? 1 : 0,
        hot_item_2_display_order: formData.hotItem2DisplayOrder || null,
        hot_item_2_menu_number: formData.hotItem2MenuNumber || null,
        is_hot_item_3: formData.hotItem3Enabled ? 1 : 0,
        hot_item_3_display_order: formData.hotItem3DisplayOrder || null,
        hot_item_3_menu_number: formData.hotItem3MenuNumber || null,
        is_dt_item: formData.driveThoughEnabled ? 1 : 0,
        dt_display_order: formData.driveThoughDisplayOrder || null,
        dt_menu_number: formData.driveThoughMenuNumber || null,
      };

      // Choose the appropriate service method based on edit mode
      const serviceCall = this.isEditMode 
        ? this.saleItemService.updateGroupItemData(itemData)
        : this.saleItemService.addGroupItemData(itemData);

      const actionText = this.isEditMode ? 'updated' : 'created';

      serviceCall.subscribe({
        next: (response: any) => {
          if (response.status === 'SUCCESS') {
            const snackBarData: SnackBarData = {
              message: `Group item ${actionText} successfully`,
              title: 'Success',
              type: 'success'
            };
            this.snackBarService.openSnackBar(snackBarData);
            
            // For new items, return the response data which should contain the new ID
            const resultData = this.isEditMode ? itemData : (response.data || itemData);
            this.dialogRef.close(resultData);
          } else {
            const snackBarData: SnackBarData = {
              message: response.message || `Failed to ${this.isEditMode ? 'update' : 'create'} group item`,
              title: 'Failed!!!',
              type: 'error'
            };
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: () => {
          const snackBarData: SnackBarData = {
            message: `Failed to ${this.isEditMode ? 'update' : 'create'} group item. Connection to server failed.`,
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      });
    } else {
      this.editForm.markAllAsTouched();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get isMultiColorEnabled(): boolean {
    return this.editForm.get('useMultiColor')?.value || false;
  }

  get isEditMode(): boolean {
    return !!(this.data.groupItemId && this.data.groupItemId > 0);
  }
}
